import ErrorResponse from './Errors/ErrorResponse';
import handleHttpError from './Errors/handleHttpError';
import HttpError from './Errors/HttpError';
import { assessmentsApiUrl } from '@/AppSettings';

interface RequestParams extends Omit<RequestInit, 'method'> {
    path: string;
    accessToken?: string;
    contentType?: string;
    responseType?: string;
    body?: any;
}

interface Options {
    /** expect a blob response from the api */
    blobResponse: boolean;
}

const formRequest = ({
    contentType = 'application/json',
    accessToken,
    path,
    responseType,
    ...config
}: RequestParams & RequestInit) => {
    const request = new Request(`${assessmentsApiUrl}${path}`, config);

    request.headers.set('Content-Type', contentType);

    if (responseType) {
        request.headers.set('responseType', responseType);
    }

    if (accessToken) {
        request.headers.set('authorization', `bearer ${accessToken}`);
    }

    return request;
};

const unwrapResponse = async <T>(response: Response, opts?: Options): Promise<T> => {
    if (!response.ok) {
        const text = await response.text();

        let errorResponse: ErrorResponse;

        try {
            errorResponse = JSON.parse(text);
        } catch {
            // JSON parsing failed, throw raw text as error
            throw new Error(text);
        }

        // If we successfully parsed the error response, handle it
        const error = handleHttpError(errorResponse!);
        throw error;
    }

    if (opts?.blobResponse) {
        return response.blob() as T;
    }

    switch (response.status) {
        case 201:
        case 202:
        case 204:
            return Promise.resolve() as T;
        default:
            try {
                return response.headers.get('Content-Length') !== '0' && (await response.json());
            } catch (error) {
                // catch empty body returned by the api. If the body is purposefully not in JSON format, then
                // you will need to handle it manually with Options and return it conditionally
                if (error instanceof SyntaxError) {
                    return Promise.resolve() as T;
                } else {
                    throw error;
                }
            }
    }
};

const buildOptions = (opts?: Partial<Options>): Options => ({ blobResponse: false, ...opts });

const http = {
    get: async <T>(params: RequestParams, paramOpts?: Partial<Options>): Promise<T> => {
        const opts = buildOptions(paramOpts);
        const request = formRequest({ ...params, method: 'get' });
        const response = await unwrapResponse<T>(await fetch(request), opts);
        return response;
    },
    post: async <T>(params: RequestParams, paramOpts?: Partial<Options>): Promise<T> => {
        const opts = buildOptions(paramOpts);
        const request = formRequest({ ...params, body: JSON.stringify(params.body), method: 'post' });
        const response = await unwrapResponse<T>(await fetch(request), opts);
        return response;
    },
    put: async <T>(params: RequestParams, paramOpts?: Partial<Options>): Promise<T> => {
        const opts = buildOptions(paramOpts);
        const request = formRequest({ ...params, body: JSON.stringify(params.body), method: 'put' });
        const response = await unwrapResponse<T>(await fetch(request), opts);
        return response;
    },
    patch: async <T>(params: RequestParams, paramOpts?: Partial<Options>): Promise<T> => {
        const opts = buildOptions(paramOpts);
        const request = formRequest({ ...params, body: JSON.stringify(params.body), method: 'patch' });
        const response = await unwrapResponse<T>(await fetch(request), opts);
        return response;
    },
    delete: async <T>(params: RequestParams, paramOpts?: Partial<Options>): Promise<T> => {
        const opts = buildOptions(paramOpts);
        const request = formRequest({ ...params, body: JSON.stringify(params.body), method: 'delete' });
        const response = await unwrapResponse<T>(await fetch(request), opts);
        return response;
    },
    getFile: async (params: RequestParams): Promise<Blob> => {
        const request = formRequest({ ...params, method: 'get' });
        const response = await fetch(request);
        if (!response.ok) {
            throw new HttpError('Unable to get a file', response.status, response.type);
        }

        return response.blob();
    },
    postFile: async (params: RequestParams): Promise<Blob> => {
        const request = formRequest({ ...params, body: JSON.stringify(params.body), method: 'post' });
        const response = await fetch(request);
        if (!response.ok) {
            throw new HttpError('Unable to get a file', response.status, response.type);
        }

        return response.blob();
    },
};

export default http;
